<template>
  <InfuseModal :title="t('e_connector.chargingStationLinks.modal.bulkUpdate.title')" @close="emit('close')">
    <form ref="form" id="bulk-update-links" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] w-[600px] flex-col gap-4 overflow-y-auto px-5">
        <InfuseNote>
          {{ t('e_connector.chargingStationLinks.modal.bulkUpdate.description', { count: linkedCentralSystems.length }) }}
        </InfuseNote>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStationLink.commandsAllowed')"
            class="basis-1/3"
            size="small"
          >
            <InfuseSelect
              name="commandsAllowed"
              size="small"
              v-model="commandsAllowed"
              :options="triStateOptions"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.common.chargingStationLink.authorizationAllowed')"
            class="basis-1/3"
            size="small"
          >
            <InfuseSelect
              name="sessionAuthorizationAllowed"
              size="small"
              v-model="sessionAuthorizationAllowed"
              :options="triStateOptions"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.common.chargingStationLink.forwardBasicAuth')"
            class="basis-1/3"
            size="small"
          >
            <InfuseSelect
              name="forwardBasicAuth"
              size="small"
              v-model="forwardBasicAuth"
              :options="triStateOptions"
            />
          </InfuseField>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton type="submit" form="bulk-update-links" :disabled="loading || !hasChanges" :loading="loading">
        {{ t('e_connector.common.action.update') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { InfuseButton, InfuseField, InfuseModal, InfuseNote, InfuseSelect } from '@infuse/ui/components';
import { LinkedCentralSystem } from '@infuse/e-connector/central-system/domain';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

/** Fields left undefined keep their current value on each link. */
export interface BulkLinkedCentralSystemChanges {
  commandsAllowed?: boolean;
  sessionAuthorizationAllowed?: boolean;
  forwardBasicAuth?: boolean;
}

const UNCHANGED = 'unchanged';
const ENABLED = 'enabled';
const DISABLED = 'disabled';

interface Props {
  loading: boolean;
  linkedCentralSystems: LinkedCentralSystem[];
}

interface Emits {
  (e: 'close'): void;
  (e: 'update', changes: BulkLinkedCentralSystemChanges): void;
}

withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

const commandsAllowed = ref<string>(UNCHANGED);
const sessionAuthorizationAllowed = ref<string>(UNCHANGED);
const forwardBasicAuth = ref<string>(UNCHANGED);

const triStateOptions = computed(() => [
  { key: t('e_connector.chargingStationLinks.modal.bulkUpdate.field.unchanged'), value: UNCHANGED },
  { key: t('e_connector.chargingStationLinks.modal.bulkUpdate.field.enabled'), value: ENABLED },
  { key: t('e_connector.chargingStationLinks.modal.bulkUpdate.field.disabled'), value: DISABLED }
]);

const hasChanges = computed(() =>
  [commandsAllowed.value, sessionAuthorizationAllowed.value, forwardBasicAuth.value].some(
    (value) => value !== UNCHANGED
  )
);

function toBoolean(value: string): boolean | undefined {
  return value === UNCHANGED ? undefined : value === ENABLED;
}

function onSave() {
  const changes: BulkLinkedCentralSystemChanges = {};

  const commands = toBoolean(commandsAllowed.value);
  if (commands !== undefined) {
    changes.commandsAllowed = commands;
  }

  const authorization = toBoolean(sessionAuthorizationAllowed.value);
  if (authorization !== undefined) {
    changes.sessionAuthorizationAllowed = authorization;
  }

  const basicAuth = toBoolean(forwardBasicAuth.value);
  if (basicAuth !== undefined) {
    changes.forwardBasicAuth = basicAuth;
  }

  emit('update', changes);
}
</script>
